import * as React from 'react';
import { Web3AuthProvider as Web3AuthReactProvider } from '@web3auth/modal-react-hooks';
import { CHAIN_NAMESPACES, WEB3AUTH_NETWORK } from '@web3auth/base';
import { EthereumPrivateKeyProvider } from '@web3auth/ethereum-provider';
import type { Web3AuthContextConfig } from '@web3auth/modal-react-hooks';
import type { Web3AuthOptions } from '@web3auth/modal';

/**
 * Chain configuration for Ethereum mainnet
 */
const chainConfig = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: '0x1', // ETH mainnet
  rpcTarget: 'https://rpc.ankr.com/eth',
  displayName: 'Ethereum Mainnet',
  blockExplorerUrl: 'https://etherscan.io',
  ticker: 'ETH',
  tickerName: 'Ethereum',
} as const;

/**
 * Create the Ethereum Private Key Provider
 * Required by Web3Auth SDK v9+ for key derivation
 */
const privateKeyProvider = new EthereumPrivateKeyProvider({
  config: { chainConfig },
});

/**
 * Web3Auth configuration for ANYW3AR
 *
 * Uses No-Modal SDK for headless operation with custom Cognito JWT auth.
 * Network: SAPPHIRE_MAINNET for production.
 *
 * CRITICAL: Key export must be enabled in Web3Auth Dashboard:
 * Dashboard -> Project Settings -> Advanced -> Key Export
 *
 * @see https://docs.metamask.io/embedded-wallets/dashboard/advanced/key-export/
 */
const web3AuthOptions: Web3AuthOptions = {
  clientId: import.meta.env.VITE_WEB3AUTH_CLIENT_ID || '',
  web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_MAINNET,
  chainConfig,
  privateKeyProvider,
};

/**
 * Web3Auth context configuration
 * Combines Web3Auth options with any additional context config
 */
const web3AuthContextConfig: Web3AuthContextConfig = {
  web3AuthOptions,
};

interface Web3AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Web3Auth Provider Component
 *
 * Wraps the application with Web3Auth context for embedded wallet functionality.
 * Must be placed in the component tree AFTER React providers but BEFORE
 * components that need Web3Auth functionality.
 *
 * This provider gracefully handles missing client ID by logging a warning
 * and rendering children directly, allowing development/testing without
 * a real Web3Auth account.
 *
 * @example
 * ```tsx
 * <React.StrictMode>
 *   <Provider store={store}>
 *     <Web3AuthProvider>
 *       <App />
 *     </Web3AuthProvider>
 *   </Provider>
 * </React.StrictMode>
 * ```
 */
export function Web3AuthProvider({ children }: Web3AuthProviderProps) {
  // Skip provider if client ID is not configured (dev/test environments)
  if (!import.meta.env.VITE_WEB3AUTH_CLIENT_ID) {
    console.warn(
      '[Web3AuthProvider] VITE_WEB3AUTH_CLIENT_ID not set. Web3Auth disabled.'
    );
    return <>{children}</>;
  }

  return (
    <Web3AuthReactProvider config={web3AuthContextConfig}>
      {children}
    </Web3AuthReactProvider>
  );
}
